import { isCpu, type PlayerPublic, type Seat } from '@xox/shared'
import { Avatar } from './Avatar'
import { Button } from './ui'

interface Props {
  mark: Seat
  /** `xo` letters (XOX) or coloured `disc`s (Connect Four). */
  style?: 'xo' | 'disc'
  player: PlayerPublic | null
  isYou: boolean
  online: boolean
  active: boolean
  ready: boolean
  canTake: boolean
  onTake: () => void
}

export function SeatCard({
  mark,
  style = 'xo',
  player,
  isYou,
  online,
  active,
  ready,
  canTake,
  onTake,
}: Props) {
  const disc = style === 'disc'
  const color = disc ? '' : mark === 'X' ? 'text-x' : 'text-o'
  const ring = disc
    ? mark === 'X'
      ? 'ring-[var(--c4-red)]'
      : 'ring-[var(--c4-yellow)]'
    : mark === 'X'
      ? 'ring-x'
      : 'ring-o'
  const badge = disc
    ? mark === 'X'
      ? 'bg-[var(--c4-red)]'
      : 'bg-[var(--c4-yellow)] !text-slate-900'
    : mark === 'X'
      ? 'bg-x'
      : 'bg-o'
  const label = disc ? (mark === 'X' ? 'red' : 'yellow') : mark
  const cpu = isCpu(player?.id)
  const status = !online
    ? 'Offline'
    : ready
      ? 'Ready'
      : active
        ? 'Thinking…'
        : cpu
          ? 'Unbeaten'
          : 'Online'

  return (
    <div
      className={[
        'flex min-w-0 flex-1 flex-col items-center gap-2 rounded-2xl bg-white p-2.5 text-center ring-1 transition-shadow',
        'sm:flex-row sm:gap-3 sm:p-3 sm:text-left dark:bg-slate-900',
        active ? `${ring} shadow-md ring-2` : 'ring-slate-200 dark:ring-slate-800',
      ].join(' ')}
      aria-live={active ? 'polite' : undefined}
    >
      <div className="relative shrink-0">
        {player ? (
          <Avatar
            id={player.id}
            avatar={player.avatar}
            color={player.color}
            size="lg"
            thinking={cpu && active}
          />
        ) : (
          <div
            className={`flex size-12 items-center justify-center rounded-full bg-slate-100 text-xl font-black dark:bg-slate-800 ${color}`}
          >
            {disc ? <span className={`size-6 rounded-full ${badge}`} aria-hidden /> : mark}
          </div>
        )}
        {player && (
          <span
            role="img"
            className={`absolute -right-1 -bottom-1 flex size-5 items-center justify-center rounded-full text-[11px] font-black text-white ring-2 ring-white dark:ring-slate-900 ${badge}`}
            aria-label={`plays ${label}`}
          >
            {disc ? '' : mark}
          </span>
        )}
      </div>
      <div className="min-w-0 w-full flex-1">
        {player ? (
          <>
            <div className="flex flex-wrap items-center justify-center gap-x-1.5 sm:justify-start">
              <span className="max-w-full truncate text-sm font-semibold sm:text-base">
                {player.nickname}
              </span>
              {isYou && (
                <span className="rounded-full bg-slate-200 px-1.5 text-[10px] font-bold uppercase dark:bg-slate-700">
                  you
                </span>
              )}
              {cpu && (
                <span className="rounded-full bg-violet-100 px-1.5 text-[10px] font-bold uppercase text-violet-700 dark:bg-violet-950/60 dark:text-violet-300">
                  cpu
                </span>
              )}
            </div>
            <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500 sm:justify-start">
              <span
                className={`inline-block size-2 shrink-0 rounded-full ${online ? 'bg-emerald-500' : 'bg-slate-400'}`}
                aria-hidden
              />
              <span className="truncate">{status}</span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1.5 sm:flex-row sm:justify-between">
            <span className="text-sm text-slate-500">Open seat</span>
            {canTake && (
              <Button variant="secondary" className="min-h-9 px-3 py-1 text-xs" onClick={onTake}>
                Take seat
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
